import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, Pencil } from "lucide-react";

/**
 * Click-to-edit numeric cell. Saves via PATCH /api/admin/lots/{lot_number}
 */
export const InlineRentEditor = ({ lot, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(lot.monthly_rent));
  const [saving, setSaving] = useState(false);

  const commit = async () => {
    const num = parseFloat(value);
    if (Number.isNaN(num) || num <= 0) {
      toast.error("Enter a valid rent amount");
      return;
    }
    if (num === lot.monthly_rent) { setEditing(false); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/lots/${lot.lot_number}`, { monthly_rent: num });
      onSave(data);
      toast.success(`Lot ${lot.lot_number} rent updated`);
      setEditing(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        className="inline-flex items-center gap-2 text-[#0B0C0E] hover:text-[#56606E] group"
        data-testid={`edit-rent-${lot.lot_number}`}
      >
        ${lot.monthly_rent.toLocaleString()}/mo
        <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100 transition" />
      </button>
    );
  }

  return (
    <div className="inline-flex items-center gap-1">
      <span className="text-[#56606E] text-sm">$</span>
      <input
        type="number" step="1" value={value} autoFocus disabled={saving}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") setEditing(false);
        }}
        className="w-24 bg-white border-2 border-[#0B0C0E] px-2 py-1 text-sm text-[#0B0C0E] outline-none"
        data-testid={`edit-rent-input-${lot.lot_number}`}
      />
      <button onClick={commit} disabled={saving} className="text-emerald-700 hover:bg-emerald-100 p-1" data-testid={`save-rent-${lot.lot_number}`}>
        <Check className="w-4 h-4" />
      </button>
      <button onClick={() => { setEditing(false); setValue(String(lot.monthly_rent)); }} className="text-red-700 hover:bg-red-100 p-1">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};
